// Public dosing reference for medications.
//
// Each limit's maxDailyDoseMg is the HIGHEST maintenance dose the FDA label
// sanctions for any indication — deliberately generous, so the dose-sanity
// warning only fires on entries no prescription could explain (the form warns
// at more than double this number). This is a typo catcher, not dosing advice.
//
// Two tiers:
//   1. DOSE_LIMITS below — hand-curated, read from the label by a person.
//      Always wins, and is synchronous so it never waits on the network.
//   2. The drugReference collection in Firestore — mined from openFDA drug
//      labels by the syncDrugReference scheduled function (functions/src/openfda)
//      and keyed by the same `rxcui` string the medication search saves. Read it
//      through services/drugReferenceService or hooks/useDoseLimits.
//      Per-kg doses and interaction-conditional maxima are excluded there, and a
//      medication whose label had no matchable maximum has no ceiling, so the
//      warning stays silent for it.

import i18n from "i18n";

const DOSE_LIMITS = [
  {
    // Lamictal ODT Orange Patient Titration Kit (for patients not taking
    // enzyme-inducing drugs or valproate) — lamotrigine 25/50/100 mg ODT.
    // Label maintenance for that population: epilepsy adjunctive 225–375
    // mg/day, epilepsy monotherapy 500 mg/day, bipolar 200 mg/day (doses
    // above 200 not recommended).
    rxcuis: ["851751"],
    nameMatch: /lamictal\s+odt\s+orange\s+patient\s+titration\s+kit/i,
    genericName: "lamotrigine",
    maxDailyDoseMg: 500,
    source:
      "https://dailymed.nlm.nih.gov/dailymed/lookup.cfm?setid=d7e3572d-56fe-4727-2bb4-013ccca22678",
  },
  {
    // Every other lamotrigine product. The miner reads 200 mg off the bipolar
    // sentence ("doses above 200 mg/day are not recommended"), which would
    // false-warn an epileptic patient on a 500 mg/day monotherapy maintenance
    // dose. The highest labelled maintenance is Lamictal XR adjunctive with an
    // enzyme inducer, up to 600 mg/day.
    rxcuis: [],
    nameMatch: /lamotrigine|lamictal/i,
    genericName: "lamotrigine",
    maxDailyDoseMg: 600,
    source:
      "https://dailymed.nlm.nih.gov/dailymed/drugInfo.cfm?setid=d7e3572d-56fe-4727-2bb4-013ccca22678",
  },
];

/**
 * The hand-curated dose reference for a medication, by rxcui or by display
 * name, or null. Synchronous: the curated tier ships with the app.
 */
export function findCuratedDoseLimit({ rxcui, name } = {}) {
  return (
    DOSE_LIMITS.find(
      (entry) =>
        (rxcui && entry.rxcuis.includes(String(rxcui))) ||
        (name && entry.nameMatch.test(name))
    ) || null
  );
}

/**
 * A drugReference document (see functions/src/openfda/sync.js) as a dose
 * limit, or null when the reference has no ceiling.
 */
export function doseLimitFromReference(reference) {
  if (reference?.maxDailyDoseMg == null) return null;
  return {
    genericName: reference.genericName || "this medication",
    maxDailyDoseMg: reference.maxDailyDoseMg,
    source: reference.dailymedUrl || null,
  };
}

const MASS_PATTERN = /(\d+(?:[.,]\d+)?)\s*(mg|mcg|µg|ug|g)\b/i;
const UNIT_TO_MG = { mg: 1, g: 1000, mcg: 0.001, "µg": 0.001, ug: 0.001 };

/**
 * Pull the mass out of a free-text dosage ("200mg" → 200, "0.5 g" → 500).
 * Returns null when no recognizable mass is present — a bare number could be
 * tablets or drops, and a warning built on a guess would cry wolf.
 */
export function parseDoseMg(dosage) {
  const match = MASS_PATTERN.exec(String(dosage || ""));
  if (!match) return null;
  const amount = parseFloat(match[1].replace(",", "."));
  if (!Number.isFinite(amount)) return null;
  return amount * UNIT_TO_MG[match[2].toLowerCase()];
}

/**
 * The dose-sanity warning for an entered dosage against a limit, or null.
 * More than double the highest labelled maintenance dose is almost always a
 * typo; anything below that is left alone.
 */
export function doseWarningText(limit, dosage) {
  const enteredMg = parseDoseMg(dosage);
  if (!limit || enteredMg == null || enteredMg <= limit.maxDailyDoseMg * 2) return null;
  // doseLimitFromReference's placeholder name is English; show it in the reader's language.
  const name =
    limit.genericName === "this medication"
      ? i18n.t("medications:doseWarning.thisMedication")
      : limit.genericName;
  return i18n.t("medications:doseWarning.text", { name, mg: limit.maxDailyDoseMg });
}
